const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Runs z-score anomaly detection on a farmer's daily records for a single month.
 * Requires at least 3 data points; flags entries where |z| >= 2.
 * @param {Object} dailyRecords - { [day]: value } map (day as string key, litre value)
 * @param {number} daysInMonth - total days in the month
 * @returns {{ mean, stdDev, entries: Array<{day, value, zScore, isAnomaly}>, anomalyCount, sampleSize }}
 */
export const detectFarmerAnomalies = (dailyRecords = {}, daysInMonth = 31) => {
  const entries = [];
  for (let day = 1; day <= daysInMonth; day += 1) {
    const raw = dailyRecords[String(day)];
    const value = Number(raw);
    if (Number.isFinite(value) && value > 0) {
      entries.push({ day, value });
    }
  }

  // Require at least 3 data points — consistent with detectMilkRecordAnomalies in
  // farmerViewModalHelpers.js — before flagging any entry as anomalous.
  if (entries.length < 3) {
    return {
      mean: 0,
      stdDev: 0,
      entries: entries.map((e) => ({ ...e, zScore: 0, isAnomaly: false })),
      anomalyCount: 0,
      sampleSize: entries.length,
    };
  }

  const mean = entries.reduce((sum, e) => sum + e.value, 0) / entries.length;
  const variance =
    entries.reduce((sum, e) => sum + (e.value - mean) ** 2, 0) / entries.length;
  const stdDev = Math.sqrt(variance);

  if (!stdDev) {
    return {
      mean,
      stdDev,
      entries: entries.map((e) => ({ ...e, zScore: 0, isAnomaly: false })),
      anomalyCount: 0,
      sampleSize: entries.length,
    };
  }

  const enrichedEntries = entries.map((e) => {
    const zScore = (e.value - mean) / stdDev;
    return { ...e, zScore, isAnomaly: Math.abs(zScore) >= 2 };
  });

  return {
    mean,
    stdDev,
    entries: enrichedEntries,
    anomalyCount: enrichedEntries.filter((e) => e.isAnomaly).length,
    sampleSize: entries.length,
  };
};

/**
 * Iterates all farmers, runs per-farmer anomaly detection, and returns a flat
 * array of all records (anomalous and normal) with farmer identity attached.
 * @param {Object} farmersMap - { [otherName]: { CloudName, ... } } from ValidatedFarmers
 * @param {Object} farmerRecordsMap - { [otherName]: { [day]: value } } fetched per farmer
 * @param {number} year
 * @param {number} month - 1-based
 * @returns {Array<{day, liters, farmerKey, farmerDisplayName, farmerCloudName, zScore, isAnomaly, mean, stdDev}>}
 */
export const buildAllFarmerAnomalyRecords = (
  farmersMap = {},
  farmerRecordsMap = {},
  year,
  month
) => {
  const daysInMonth = getDaysInMonth(year, month);
  const result = [];

  Object.entries(farmersMap).forEach(([farmerKey, farmer]) => {
    const dailyRecords = farmerRecordsMap[farmerKey] || {};
    const cloudName = String(farmer?.CloudName || "").trim();
    const displayName = cloudName || farmerKey;

    const anomalyResult = detectFarmerAnomalies(dailyRecords, daysInMonth);
    anomalyResult.entries.forEach((entry) => {
      result.push({
        day: entry.day,
        liters: entry.value,
        farmerKey,
        farmerDisplayName: displayName,
        farmerCloudName: cloudName,
        zScore: entry.zScore,
        isAnomaly: entry.isAnomaly,
        mean: anomalyResult.mean,
        stdDev: anomalyResult.stdDev,
      });
    });
  });

  return result.sort((a, b) => {
    if (a.day !== b.day) return a.day - b.day;
    return a.farmerDisplayName.localeCompare(b.farmerDisplayName);
  });
};

/** Returns the number of days in the given month (month is 1-based). */
export const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

/**
 * Aggregates daily totals from a Firebase MilkCollected month snapshot value.
 * The snapshot value has shape: { [day]: { [recordKey]: { TotalKgs, ... } } }
 * Returns a plain object keyed by day number (integer) with total litre values.
 */
export const aggregateDailyTotals = (monthSnapshot) => {
  const result = {};
  const data = monthSnapshot || {};
  Object.entries(data).forEach(([dayKey, dayRecords]) => {
    const day = parseInt(dayKey, 10);
    if (!Number.isFinite(day) || day < 1 || day > 31) return;
    if (typeof dayRecords !== "object" || dayRecords === null) return;
    let total = 0;
    Object.values(dayRecords).forEach((record) => {
      if (typeof record === "object" && record !== null) {
        total += parseFloat(record.TotalKgs || 0) || 0;
      }
    });
    result[day] = total;
  });
  return result;
};

/**
 * Computes z-scores for each day in a month and flags anomalies (|z| >= 2).
 * @param {Object} dayTotals - { [dayNumber]: totalLitres }
 * @param {number} daysInMonth - total days in the month
 * @returns {Array<{day, total, zScore, isAnomaly}>}
 */
export const computeDailyAnomalies = (dayTotals = {}, daysInMonth = 31) => {
  const allDays = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    return { day, total: toNumber(dayTotals[day]) };
  });

  const nonZero = allDays.filter((d) => d.total > 0);
  if (nonZero.length < 3) {
    return allDays.map((d) => ({ ...d, isAnomaly: false, zScore: 0 }));
  }

  const mean = nonZero.reduce((s, d) => s + d.total, 0) / nonZero.length;
  const variance =
    nonZero.reduce((s, d) => s + (d.total - mean) ** 2, 0) / nonZero.length;
  const stdDev = Math.sqrt(variance);

  return allDays.map((d) => {
    if (d.total === 0 || !stdDev) {
      return { ...d, isAnomaly: false, zScore: 0 };
    }
    const zScore = (d.total - mean) / stdDev;
    return { ...d, zScore, isAnomaly: Math.abs(zScore) >= 2 };
  });
};

/**
 * Returns a fill color for an anomaly circle based on absolute z-score severity.
 * Medium (2–3): orange, High (3–4): deep-orange, Critical (≥4): red.
 */
export const getAnomalyColor = (absZScore) => {
  if (absZScore >= 4) return "#dc2626"; // red-600
  if (absZScore >= 3) return "#ea580c"; // orange-600
  return "#f97316"; // orange-500
};

export const normalizeMonthlyInsights = (months = []) => {
  const byMonth = new Map();
  months.forEach((item) => {
    const month = Number(item?.month);
    if (month >= 1 && month <= 12) {
      byMonth.set(month, item);
    }
  });

  return MONTH_LABELS.map((label, index) => {
    const month = index + 1;
    const item = byMonth.get(month) || {};
    return {
      month,
      monthLabel: label,
      totalKgs: toNumber(item.total_kgs),
      avgDailyKgs: toNumber(item.avg_daily_kgs),
      anomalyCount: toNumber(item.anomaly_count),
      readingDays: toNumber(item.reading_days),
    };
  });
};

export const buildMilkInsightsSummary = (months = []) => {
  const totalKgs = months.reduce((sum, month) => sum + toNumber(month.totalKgs), 0);
  const totalAnomalies = months.reduce(
    (sum, month) => sum + toNumber(month.anomalyCount),
    0
  );
  return {
    totalKgs,
    totalAnomalies,
    averageMonthlyKgs: months.length ? totalKgs / months.length : 0,
  };
};
